import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Eye, FileText, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useToast } from 'src/context/ToastContext';

import {
	type StatementFile,
	type StatementListParams,
	useDeleteStatementMutation,
	useGetEnterpriseUsersQuery,
	useGetStatementsQuery,
	useRetryStatementMutation,
} from '../api';
import { useSubscription } from '../hooks';
import { useConfigs } from '../hooks/useConfigs';
import { useAuth } from '../providers/AppAuthProvider';
import { formatRatePlanType, formatRatePlanTypes } from '../utils/utils';

import {
	Alert,
	Button,
	FilterDropdown,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	PageContent,
	PageHeader,
	SearchInput,
	SkeletonTable,
	StatusPill,
	Typography,
} from './common';
import ConfigSetupModal from './ConfigSetupModal';
import CustomizeFeeStructureModal from './CustomizeFeeStructureModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import NoConfigModal from './NoConfigModal';
import StatementUpload from './StatementUpload';

import type { FilterGroup, FilterOption } from './common';

// Transform StatementFile to Document interface for backward compatibility
interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
	rate_plan_type?: string;
	selected_rate_types?: string[];
	my_revenue?: number | null;
}

const getStatusColor = (status: string) => {
	switch (status) {
		case 'Processed':
			return 'bg-green-100 text-green-800 border-green-200';
		case 'Processing':
			return 'bg-yellow-100 text-yellow-800 border-yellow-200';
		case 'Error':
			return 'bg-red-100 text-red-800 border-red-200';
		default:
			return 'bg-gray-100 text-gray-800 border-gray-200';
	}
};

const transformToDocument = (statement: StatementFile): Document => {
	return {
		id: statement.id,
		name: statement.original_filename,
		type: statement.analysis_type,
		uploaded_by: statement.uploaded_by,
		date: new Date(statement.created_at).toISOString().split('T')[0],
		size: `${(statement.file_size / 1024).toFixed(1)} KB`,
		status:
			statement.current_status === 'processed'
				? 'completed'
				: statement.current_status === 'processing'
					? 'processing'
					: statement.current_status === 'error'
						? 'failed'
						: statement.current_status,
		processingMonth: new Date(statement.created_at).toLocaleDateString(
			'en-US',
			{ month: 'long', year: 'numeric' },
		),
		pdfUrl: statement.results?.signed_urls?.pdf_url,
		results: statement.results,
		rate_plan_type: statement.rate_plan_type,
		selected_rate_types: statement.selected_rate_types,
		my_revenue: statement.revenue_analysis?.total_revenue ?? null,
	};
};

interface StatementAnalyzerProps {
	onDocumentProcessed?: (documentId: string) => void;
	uploadConfig?: any;
	onFirstTimeUserUpload?: (documentId: string) => void;
	uploadedDocumentId?: string;
	onGoBackToList?: () => void;
	onUpgradePlan?: () => void;
	pageTitle?: string;
	pageSubtitle?: string;
}

const StatementAnalyzer: React.FC<StatementAnalyzerProps> = ({
	pageTitle,
	pageSubtitle,
}) => {
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showRetryModal, setShowRetryModal] = useState(false);
	const [retryDocument, setRetryDocument] = useState<Document | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
		null,
	);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [showCustomizeModal, setShowCustomizeModal] = useState(false);
	const [customizeDocument, setCustomizeDocument] = useState<Document | null>(
		null,
	);
	const [customizeConfigId, setCustomizeConfigId] = useState<string>('');

	// Filtering state
	const [filters, setFilters] = useState<StatementListParams>({
		uploaded_by: '',
		search: '',
		profit_status: '',
		// No engine filter by default: list every statement for the tenant
		// (both legacy `winstar` and `platform` engines). Forcing
		// engine='platform' here hid pre-migration / restored statements.
		page: 1,
		page_size: 50,
	});

	const [searchInput, setSearchInput] = useState('');
	const [selectedUploaders, setSelectedUploaders] = useState<string[]>([]);
	const [selectedProfitStatus, setSelectedProfitStatus] = useState<string[]>(
		[],
	);

	const navigate = useNavigate();
	const { user, isAdmin } = useAuth();

	// Fetched once and refreshed on demand via the Refresh button (and after
	// upload / delete / customize). We intentionally do NOT auto-poll — the
	// previous 10s poll reloaded the table repeatedly whenever anything was
	// processing, which was disruptive. Click Refresh to pull the latest status.
	const {
		data: statements,
		isLoading,
		refetch,
		isFetching,
	} = useGetStatementsQuery(filters);
	const { data: enterpriseUsers, isLoading: isLoadingUsers } =
		useGetEnterpriseUsersQuery(undefined, {
			skip: !isAdmin, // Only fetch enterprise users for admin users
		});
	const [deleteStatementMutation, { isLoading: isDeleting }] =
		useDeleteStatementMutation();

	// Get subscription data and derived values
	const { subscriptionStatus, refetch: refetchSubscription } =
		useSubscription();

	const canDeleteDocument = (doc: Document) => {
		return doc.uploaded_by?.email === user?.email;
	};

	// Handle delete confirmation
	const handleDeleteClick = (doc: Document) => {
		setDocumentToDelete(doc);
		setDeleteError(null);
		setShowDeleteModal(true);
	};

	const handleDeleteConfirm = async () => {
		if (!documentToDelete) return;

		setDeleteError(null);
		try {
			await deleteStatementMutation(documentToDelete.id).unwrap();
			refetch(); // Refresh the list
			setShowDeleteModal(false);
			setDocumentToDelete(null);
		} catch (error) {
			console.error('Failed to delete statement:', error);
			setDeleteError(
				(error as any)?.data?.message ||
					'Could not delete this document right now. Please try again in a moment.',
			);
		}
	};

	const handleDeleteCancel = () => {
		setShowDeleteModal(false);
		setDocumentToDelete(null);
		setDeleteError(null);
	};

	const handleCustomizeClick = (doc: Document) => {
		setCustomizeDocument(doc);
		const configIdFromDoc = (doc as any)?.config_id || configs?.[0]?.id || '';
		setCustomizeConfigId(configIdFromDoc);
		setShowCustomizeModal(true);
	};

	const handleCustomizeClose = () => {
		setShowCustomizeModal(false);
		setCustomizeDocument(null);
		setCustomizeConfigId('');
	};

	const handleCustomizeConfirm = () => {
		refetch();
		refreshConfigs();
	};

	// Get configs from store
	const { refreshConfigs, checkHasConfigs } = useConfigs();

	// Transform statements to documents
	const documents = statements?.files.map(transformToDocument) || [];

	// Filter handlers
	const handleFilterChange = (
		key: keyof StatementListParams,
		value: string | number,
	) => {
		setFilters((prev) => ({
			...prev,
			[key]: value,
			page: 1, // Reset to first page when filters change
		}));
	};

	const debouncedSearch = useCallback((searchValue: string) => {
		setFilters((prev) => ({
			...prev,
			search: searchValue,
			page: 1,
		}));
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			debouncedSearch(searchInput);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput, debouncedSearch]);

	const handleSearchChange = (search: string) => {
		setSearchInput(search);
	};

	const clearSearch = () => {
		setSearchInput('');
		setFilters((prev) => ({
			...prev,
			search: '',
			page: 1,
		}));
	};

	const clearAllFilters = () => {
		setFilters({
			uploaded_by: '',
			search: '',
			profit_status: '',
			page: 1,
			page_size: 50,
		});
		setSearchInput('');
		setSelectedUploaders([]);
		setSelectedProfitStatus([]);
	};

	// Get unique uploaded by values from enterprise users (admin only)
	const uploaderOptions: FilterOption[] = enterpriseUsers?.users
		? enterpriseUsers.users.map((user) => ({
				id: user.email,
				label: user.name,
				value: user.email,
			}))
		: [];

	// Profit filter options
	const profitOptions: FilterOption[] = [
		{ id: 'positive', label: 'Positive', value: 'positive' },
		{ id: 'negative', label: 'Negative', value: 'negative' },
	];

	// Handle filter selection change
	const handleUploaderFilterChange = (values: string[]) => {
		setSelectedUploaders(values);
		handleFilterChange('uploaded_by', values.join(','));
	};

	// Handle profit filter change (single select - use first value or empty)
	const handleProfitFilterChange = (values: string[]) => {
		setSelectedProfitStatus(values);
		// Only use the first selected value (single select behavior)
		const profitValue = values.length > 0 ? values[values.length - 1] : '';
		// If both selected, clear (show all)
		const finalValue = values.length === 2 ? '' : profitValue;
		setSelectedProfitStatus(finalValue ? [finalValue] : []);
		handleFilterChange('profit_status', finalValue);
	};

	const handlePageChange = (page: number) => {
		setFilters((prev) => ({
			...prev,
			page,
		}));
	};

	// Effect to refetch subscription status when component mounts
	useEffect(() => {
		if (refetchSubscription) {
			refetchSubscription();
		}
	}, [refetchSubscription]);

	// Effect to refresh configs when component mounts
	useEffect(() => {
		refreshConfigs();
	}, []);

	// Effect to sync selectedUploaders with filters.uploaded_by
	useEffect(() => {
		if (filters.uploaded_by) {
			const emails = filters.uploaded_by.split(',').filter(Boolean);
			setSelectedUploaders(emails);
		} else {
			setSelectedUploaders([]);
		}
	}, [filters.uploaded_by]);

	const [deleteStatement, { isLoading: deleting }] =
		useDeleteStatementMutation();
	const [retryStatement, { isLoading: retrying }] = useRetryStatementMutation();
	const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
	const [showConfigSetupModal, setShowConfigSetupModal] = useState(false);
	const [showNoConfigModal, setShowNoConfigModal] = useState(false);

	const { showToast } = useToast();

	const handleUploadClick = async () => {
		// Refetch subscription status to get latest credits
		if (refetchSubscription) {
			refetchSubscription();
		}

		// Check upload permission from subscription status API
		const canUpload = subscriptionStatus?.can_upload;
		const creditsRemaining = subscriptionStatus?.credits_remaining;

		// Check if user has any configurations (from store)
		const userHasConfigs = checkHasConfigs();

		const hasNoCredits =
			subscriptionStatus && subscriptionStatus.credits_remaining === 0;

		// Check if user has no credits first
		if (hasNoCredits) {
			setShowNoCreditsModal(true);
			return;
		}

		// For first-time users (credits = -1), allow upload without configs
		if (creditsRemaining === -1) {
			return;
		}

		// Allow upload if:
		// 1. can_upload is true AND
		// 2. credits > 0 (has credits) AND
		// 3. user has at least one configuration (for returning users)
		if (
			canUpload &&
			typeof creditsRemaining === 'number' &&
			creditsRemaining > 0 &&
			userHasConfigs
		) {
			setShowUploadModal(true);
		} else if (!userHasConfigs) {
			// Show NoConfigModal if user has no configurations (only for returning users)
			setShowNoConfigModal(true);
		} else {
			// Show config setup modal if no credits or upload not allowed
			setShowConfigSetupModal(true);
		}
	};

	// Handle navigation to subscription page
	const handleGoToSubscription = () => {
		setShowNoCreditsModal(false);
		navigate('/subscription');
	};

	const handleDelete = async (doc: Document) => {
		if (window.confirm(`Are you sure you want to delete "${doc.name}"?`)) {
			try {
				await deleteStatement(doc.id).unwrap();
				showToast(
					'File Deleted',
					`"${doc.name}" has been successfully deleted.`,
					'success',
				);
			} catch (error: any) {
				console.error('Failed to delete file:', error);
				const errorMessage =
					error?.data?.message ||
					error?.message ||
					'Failed to delete file. Please try again.';
				showToast('Delete Failed', errorMessage, 'error');
			}
		}
	};

	const handleRetry = async (doc: Document) => {
		setRetryDocument(doc);
		setShowRetryModal(true);
	};

	const confirmRetry = async () => {
		if (!retryDocument) return;

		try {
			await retryStatement(retryDocument.id).unwrap();
			showToast(
				'Retry Started',
				`"${retryDocument.name}" is now being reprocessed.`,
				'success',
			);
			setShowRetryModal(false);
			setRetryDocument(null);
		} catch (error: any) {
			const errorMessage =
				error?.data?.message ||
				error?.message ||
				'Failed to retry statement. Please try again.';
			showToast('Retry Failed', errorMessage, 'error');
		}
	};

	const handleCloseConfigModal = () => {
		setShowConfigSetupModal(false);
	};

	const handleCloseNoConfigModal = () => {
		setShowNoConfigModal(false);
	};

	const handleUploadSuccess = (uploadedFileId?: string) => {
		setShowUploadModal(false);
		navigate(`/analysis/${uploadedFileId}`);
	};

	return (
		<div className="flex min-h-0 flex-col">
			<PageHeader
				title="Upload a statement to generate report"
				variant="title"
				actions={
					<Button
						onClick={handleUploadClick}
						variant="primary"
						icon={Plus}
						aria-label="Upload Statement"
					>
						Upload Statement
					</Button>
				}
			/>

			{/* Scrollable Content Area */}
			<PageContent className="flex-1 overflow-hidden">
				<div className="flex h-full flex-col min-h-0 overflow-hidden bg-white border border-gray-200 rounded-lg">
					<div className="flex-shrink-0 px-6 pt-6">
						<div className="flex items-center justify-between">
							<div>
								<span className="text-lg font-normal leading-normal">
									{pageTitle ?? 'Your Reports'}
								</span>
								{pageSubtitle && (
									<div className="text-xs text-gray-500 mt-0.5">
										{pageSubtitle}
									</div>
								)}
							</div>
							<div className="flex items-center space-x-3">
								{/* Search Input */}
								<div className="w-64">
									<SearchInput
										placeholder="Search documents..."
										value={searchInput}
										onChange={handleSearchChange}
										onClear={clearSearch}
										rightIcon={searchInput ? X : undefined}
									/>
								</div>

								{/* Manual refresh — replaces the removed 10s auto-poll */}
								<Button
									variant="outline"
									size="md"
									icon={RotateCcw}
									onClick={() => refetch()}
									loading={isFetching}
									aria-label="Refresh list"
								>
									Refresh
								</Button>

								{/* Combined Filter Dropdown */}
								<FilterDropdown
									placeholder="Filter"
									variant="outline"
									size="md"
									groups={
										[
											...(isAdmin
												? [
														{
															id: 'uploader',
															title: 'Uploader',
															options: uploaderOptions,
															selectedValues: selectedUploaders,
															onSelectionChange: handleUploaderFilterChange,
															showSearch: true,
															searchPlaceholder: 'Search users...',
														},
													]
												: []),
											{
												id: 'profit',
												title: 'Profit Status',
												options: profitOptions,
												selectedValues: selectedProfitStatus,
												onSelectionChange: handleProfitFilterChange,
											},
										] as FilterGroup[]
									}
								/>
							</div>
						</div>
					</div>

					<div className="flex-1 min-h-0 py-6 w-full">
						{documents.length === 0 &&
						!(isFetching || isLoading) &&
						!searchInput &&
						!selectedUploaders.length &&
						!selectedProfitStatus.length ? (
							<div className="flex min-w-[1280px] relative min-h-full flex-col items-center justify-center">
								<FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
								<Typography variant="h3" className="mb-2 text-gray-600">
									No documents uploaded yet
								</Typography>
								<Typography variant="body1" className="text-gray-500">
									Upload your first financial statement to get started with
									analysis
								</Typography>
							</div>
						) : documents.length === 0 &&
						  !(isFetching || isLoading) &&
						  (searchInput ||
								selectedUploaders.length ||
								selectedProfitStatus.length) ? (
							<div className="flex min-w-[1280px] h-full flex-col items-center justify-center">
								<FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
								<Typography variant="h3" className="mb-2 text-gray-600">
									No Results Found
								</Typography>
								<Typography variant="body1" className="text-gray-500">
									No documents match your filter criteria
								</Typography>
							</div>
						) : (
							<div className="flex flex-col min-h-[500px] w-full">
								{isFetching ? (
									<div className="w-full flex min-w-[1280px] items-center justify-center">
										<SkeletonTable rows={10} columns={7} showHeader={true} />
									</div>
								) : (
									<div className="w-full overflow-x-auto">
										<table
											className="min-w-[1360px]"
											style={{ width: '100%', tableLayout: 'fixed' }}
										>
											<thead className="sticky top-0 z-10 bg-gray-50">
												<tr className="border-b border-gray-200">
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-80">
														Document
													</th>
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-44">
														Uploaded By
													</th>
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-56">
														Type
													</th>
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
														Date
													</th>
													<th className="px-6 py-4 text-center text-sm font-medium text-gray-500 uppercase tracking-wider">
														Profit
													</th>
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-32">
														Status
													</th>
													<th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-72">
														Actions
													</th>
												</tr>
											</thead>
											<tbody className="bg-white">
												{documents.map((doc) => (
													<tr
														key={doc.id}
														className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
														onClick={() => {
															if (doc.status !== 'Processing') {
																navigate(`/analysis/${doc.id}`);
															}
														}}
													>
														<td className="px-6 py-4 max-w-96 align-top">
															<div className="flex items-start gap-3">
																<div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
																	<FileText className="h-5 w-5 text-gray-600" />
																</div>
																<div className="min-w-0 flex-1 space-y-1">
																	<div className="text-sm font-medium text-gray-900 break-words whitespace-normal">
																		{doc.name}
																	</div>
																	<div className="text-sm text-gray-500">
																		Financial Document • {doc.size}
																	</div>
																</div>
															</div>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="text-sm text-gray-900">
																{doc.uploaded_by?.name || 'Unknown'}
															</span>
														</td>
														<td className="px-6 py-4 align-top">
															<span className="block text-sm text-gray-900 break-words">
																{doc.selected_rate_types &&
																doc.selected_rate_types.length > 0
																	? formatRatePlanTypes(doc.selected_rate_types)
																	: formatRatePlanType(doc.rate_plan_type)}
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="text-sm text-gray-500">
																{doc.date}
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-center">
															{doc.my_revenue !== null &&
															doc.my_revenue !== undefined ? (
																doc.my_revenue > 0 ? (
																	<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
																		Positive
																	</span>
																) : (
																	<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
																		Negative
																	</span>
																)
															) : (
																<span className="text-sm text-gray-400">—</span>
															)}
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<StatusPill
																status={
																	doc.status === 'completed' ||
																	doc.status === 'Processed'
																		? 'completed'
																		: doc.status === 'processing' ||
																			  doc.status === 'Processing'
																			? 'processing'
																			: 'failed'
																}
															>
																{doc.status === 'completed' ||
																doc.status === 'Processed'
																	? 'Completed'
																	: doc.status === 'processing' ||
																		  doc.status === 'Processing'
																		? 'Processing'
																		: doc.status}
															</StatusPill>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm font-medium w-80 min-w-[320px]">
															<div className="flex items-center space-x-2 min-w-fit">
																<Button
																	variant="outline"
																	size="xs"
																	onClick={(e) => {
																		e.stopPropagation();
																		navigate(`/analysis/${doc.id}`);
																	}}
																	icon={Eye}
																	className="flex-shrink-0"
																>
																	View Analysis
																</Button>

																{canDeleteDocument(doc) && (
																	<Button
																		variant="outline"
																		size="xs"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteClick(doc);
																		}}
																		disabled={
																			doc.status === 'Processing' || isDeleting
																		}
																		icon={Trash2}
																		className="text-error-500 hover:text-error-700 flex-shrink-0 min-w-fit"
																		title="Delete file"
																		aria-label="Delete file"
																	/>
																)}
															</div>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Pagination Controls */}
					{statements?.pagination && statements.pagination.total_pages > 1 && (
						<div className="flex-shrink-0 px-8 py-4 border-t border-gray-200">
							<div className="flex items-center justify-between">
								<div className="text-sm text-gray-600">
									Page {statements.pagination.page} of{' '}
									{statements.pagination.total_pages}
								</div>
								<div className="flex items-center space-x-2">
									<Button
										variant="secondary"
										size="xs"
										onClick={() =>
											handlePageChange(statements.pagination.page - 1)
										}
										disabled={statements.pagination.page <= 1}
									>
										← Previous
									</Button>
									<Button
										variant="secondary"
										size="xs"
										onClick={() =>
											handlePageChange(statements.pagination.page + 1)
										}
										disabled={
											statements.pagination.page >=
											statements.pagination.total_pages
										}
									>
										Next →
									</Button>
								</div>
							</div>
						</div>
					)}
				</div>
			</PageContent>

			{/* No Credits Modal */}
			<Modal
				isOpen={showNoCreditsModal}
				onClose={() => setShowNoCreditsModal(false)}
				size="md"
				title="No Uploads Left"
			>
				<ModalBody>
					<Alert variant="error" className="mb-4">
						You don't have any uploads remaining. Purchase more credits to
						continue analyzing statements.
					</Alert>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={() => setShowNoCreditsModal(false)}
					>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleGoToSubscription}>
						Purchase Credits
					</Button>
				</ModalFooter>
			</Modal>

			{/* Config Setup Modal */}
			<ConfigSetupModal
				isOpen={showConfigSetupModal}
				onClose={handleCloseConfigModal}
				showViewPlans={true}
			/>

			{/* No Config Modal */}
			<NoConfigModal
				isOpen={showNoConfigModal}
				onClose={handleCloseNoConfigModal}
			/>

			{showUploadModal && (
				<StatementUpload
					onClose={() => setShowUploadModal(false)}
					onSuccess={handleUploadSuccess}
				/>
			)}

			{/* Retry Confirmation Modal */}
			<Modal
				isOpen={showRetryModal && !!retryDocument}
				onClose={() => {
					setShowRetryModal(false);
					setRetryDocument(null);
				}}
				size="md"
			>
				<ModalHeader>
					<div className="flex items-center gap-3">
						<div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
							<RotateCcw className="h-6 w-6 text-orange-600" />
						</div>
						<div>
							<Typography variant="h3">Retry Statement Analysis</Typography>
						</div>
					</div>
				</ModalHeader>
				<ModalBody>
					<Typography variant="body1" className="text-gray-600">
						Are you sure you want to retry "{retryDocument?.name}"? This will
						restart the analysis process.
					</Typography>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={() => {
							setShowRetryModal(false);
							setRetryDocument(null);
						}}
					>
						Cancel
					</Button>
					<Button variant="primary" onClick={confirmRetry} loading={retrying}>
						Retry Analysis
					</Button>
				</ModalFooter>
			</Modal>

			{/* Delete Confirmation Modal */}
			<DeleteConfirmationModal
				isOpen={showDeleteModal}
				document={documentToDelete}
				isDeleting={isDeleting}
				errorMessage={deleteError}
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
			/>

			{/* Customize Fee Structure Modal */}
			{showCustomizeModal && (
				<CustomizeFeeStructureModal
					isOpen={showCustomizeModal}
					onClose={handleCustomizeClose}
					onConfirm={handleCustomizeConfirm}
					configId={customizeConfigId}
					fileId={customizeDocument?.id || ''}
				/>
			)}
		</div>
	);
};

export default StatementAnalyzer;
